"use client";
import Image from "next/image";
import React, { useState, useEffect, useCallback } from "react";
import { FaWhatsapp, FaPaperPlane } from "react-icons/fa";
interface Partner {
  id: number;
  logo: string;
  name?: string;
}

const partnersData: Partner[] = [
  { id: 1, logo: "@public/assets/client/ambuja.webp", name: "Partner 1" },
  { id: 2, logo: "@public/assets/client/apcpl.webp", name: "Partner 2" },
  { id: 3, logo: "@public/assets/client/apgenco.webp", name: "Partner 3" },
  { id: 4, logo: "@public/assets/client/balco.webp", name: "Partner 4" },
  { id: 5, logo: "@public/assets/client/bhel.webp", name: "Partner 5" },
  { id: 6, logo: "@public/assets/client/bhushan.webp", name: "Partner 6" },
  { id: 7, logo: "@public/assets/client/cement.webp", name: "Partner 7" },
  { id: 8, logo: "@public/assets/client/cgpl.webp", name: "Partner 8" },
  { id: 9, logo: "@public/assets/client/durghapur.webp", name: "Partner 9" },
  { id: 10, logo: "@public/assets/client/essar.webp", name: "Partner 10" },
];

// Infinite scroll ke liye dataset double
const extendedPartnersData = [...partnersData, ...partnersData];

export default function PartnersSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [visibleItems, setVisibleItems] = useState(4);
  const [email, setEmail] = useState("");

  // Screen breakpoints logic:
  // Mobile (< 640px): 2 Logos
  // Tablet (640px - 1023px): 3 Logos
  // Desktop (>= 1024px): 4 Logos
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleItems(2); // Mobile view
      } else if (window.innerWidth < 1024) {
        setVisibleItems(3); // Tablet view
      } else {
        setVisibleItems(4); // Desktop view
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev >= partnersData.length) {
        return 0; // Seamless loop reset
      }
      return prev + 1;
    });
  }, []);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev <= 0) {
        return partnersData.length - 1;
      }
      return prev - 1;
    });
  }, []);

  // Auto-slide Timer
  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      handleNext();
    }, 3000);

    return () => clearInterval(interval);
  }, [handleNext, isHovered]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Subscribed Email:", email);
    setEmail("");
  };

  return (
    <div className="w-full bg-white overflow-hidden">
      {/* 1. Partners / Companies Section */}
      <section className="w-full py-12 md:py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <span className="text-[#ffa20d] font-bold text-sm tracking-wider uppercase block mb-1">
              Clients
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a1a1a] leading-tight">
              Trusted Clients
            </h2>
          </div>

          {/* Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Column: Description & Buttons */}
            <div className="lg:col-span-4 flex flex-col justify-between pr-0 lg:pr-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-6 md:mb-8">
                Lorem ipsum dolor sit amet, consectetur dolor elit. Integer
                adipiscing erat eget risus sollicitudin pellentesque et non erat
                consectetur amet.
              </p>

              {/* Carousel Buttons */}
              <div className="flex items-center gap-2 mb-6 lg:mb-0">
                <button
                  onClick={handlePrev}
                  className="bg-[#333a42] hover:bg-[#ffa20d] text-white text-xs font-semibold px-6 py-3 transition-colors duration-300 rounded-none cursor-pointer"
                >
                  Prev
                </button>
                <button
                  onClick={handleNext}
                  className="bg-[#333a42] hover:bg-[#ffa20d] text-white text-xs font-semibold px-6 py-3 transition-colors duration-300 rounded-none cursor-pointer"
                >
                  Next
                </button>
              </div>
            </div>

            {/* Right Column: Logos Slider */}
            <div
              className="lg:col-span-8 overflow-hidden"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * (100 / visibleItems)}%)`,
                }}
              >
                {extendedPartnersData.map((partner, index) => (
                  <div
                    key={`${partner.id}-${index}`}
                    /* Mobile: w-1/2 (2 logos), Tablet: sm:w-1/3 (3 logos), Desktop: lg:w-1/4 (4 logos) */
                    className="w-1/2 sm:w-1/3 lg:w-1/4 shrink-0 px-1 sm:px-2"
                  >
                    <div className="border border-gray-200 aspect-square flex flex-col items-center justify-center p-3 bg-white hover:border-[#ffa20d] transition-colors duration-300">
                      {partner.logo ? (
                        <Image
                          src={partner.logo}
                          alt={partner.name || `Partner ${partner.id}`}
                          width={160}
                          height={160}
                          className="max-h-16 sm:max-h-24 w-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                        />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Responsive Slanted Contact & Subscribe Banner */}
      <section className="w-full relative min-h-[120px] flex flex-col lg:flex-row items-stretch bg-white">
        {/* Left Side: WhatsApp Number */}
        <div className="w-full lg:w-1/2 py-6 px-6 lg:pl-16 flex items-center justify-center lg:justify-start z-10">
          <a
            href="https://wa.me/0123456789"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-[#1a1a1a] text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight hover:text-[#ffa20d] transition-colors duration-300"
          >
            <FaWhatsapp className="text-[#ffa20d] text-3xl sm:text-4xl lg:text-5xl shrink-0" />
            <span>0123-456-789</span>
          </a>
        </div>

        {/* Right Side: Skewed Orange Banner with Form */}
        <div className="w-full lg:w-3/5 bg-[#ff9e0d] py-6 sm:py-8 px-4 sm:px-6 lg:px-12 relative flex items-center justify-center lg:justify-end [clip-path:none] lg:[clip-path:polygon(12%_0,_100%_0,_100%_100%,_0%_100%)]">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-lg flex items-stretch border border-white/80 rounded-none overflow-hidden sm:-skew-x-12"
          >
            {/* Input Email */}
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full bg-transparent text-white placeholder-white/90 italic font-medium px-4 sm:px-6 py-3 outline-none sm:skew-x-12 border-none text-sm sm:text-base"
            />

            {/* Subscribe Button */}
            <button
              type="submit"
              className="bg-[#fdeed6] hover:bg-white text-[#ff9e0d] font-bold italic text-xs sm:text-sm tracking-wider uppercase px-5 sm:px-8 py-3 transition-colors duration-300 flex items-center gap-2 shrink-0 cursor-pointer"
            >
              <div className="sm:skew-x-12 flex items-center gap-2">
                <FaPaperPlane className="text-xs" />
                <span>SUBSCRIBE</span>
              </div>
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
